import type { AppRouter } from '@alison/api/router';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import type { inferInput, inferOutput } from '@trpc/tanstack-react-query';
import { useTRPC } from '../../../lib/trpc';

type TRPC = ReturnType<typeof useTRPC>;

type QueryResult<TData> = UseQueryResult<TData, TRPCClientErrorLike<AppRouter>>;

export type Aggregation = 'total' | 'average';

export type TopMetricsScope = inferInput<TRPC['creatives']['topMetrics']>;
export type TopMetricsData = inferOutput<TRPC['creatives']['topMetrics']>;
export type TopMetric = TopMetricsData['metrics'][number];
export type AggregatedMetric = TopMetric['total'];

export type CreativesListScope = inferInput<TRPC['creatives']['list']>;
export type CreativesListData = inferOutput<TRPC['creatives']['list']>;
export type CreativeRow = CreativesListData['creatives'][number];
/** What the detail drawer needs. A caller without delivery data passes null days live. */
export type DetailCreative = Omit<CreativeRow, 'activeDays'> & { activeDays: number | null };

export type CreativeScoresScope = inferInput<TRPC['creatives']['creativeScores']>;
export type CreativeScoresData = inferOutput<TRPC['creatives']['creativeScores']>;
export type CreativeScoreTileScope = inferInput<TRPC['creatives']['creativeScoreTile']>;
export type CreativeScoreTileData = inferOutput<TRPC['creatives']['creativeScoreTile']>;

export type MetricTrendRequest = inferInput<TRPC['creatives']['metricTrend']>;
export type MetricTrendScope = Omit<MetricTrendRequest, 'metrics'> & {
  metric: MetricTrendRequest['metrics'][number];
};
export type MetricTrendResponse = inferOutput<TRPC['creatives']['metricTrend']>;
export type MetricTrendData = MetricTrendResponse['metrics'][number] &
  Pick<MetricTrendResponse, 'currencySymbol' | 'granularity'>;

export type MetricCatalog = inferOutput<TRPC['filters']['metrics']>;

/** `accountIds` absent is the entitled scope, as on the server; an empty list waits for a pick. */
export function useMetricCatalog(accountIds?: number[]): QueryResult<MetricCatalog> {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.filters.metrics.queryOptions(accountIds === undefined ? undefined : { accountIds }),
    enabled: accountIds === undefined || accountIds.length > 0,
  });
}

export function useTopMetrics(
  scope: TopMetricsScope,
  enabled: boolean,
): QueryResult<TopMetricsData> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.creatives.topMetrics.queryOptions(scope), enabled });
}

export function useCreativesList(
  scope: CreativesListScope,
  enabled: boolean,
): QueryResult<CreativesListData> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.creatives.list.queryOptions(scope), enabled });
}

export function useCreativeScores(
  scope: CreativeScoresScope,
  enabled: boolean,
): QueryResult<CreativeScoresData> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.creatives.creativeScores.queryOptions(scope), enabled });
}

export function useCreativeScoreTile(
  scope: CreativeScoreTileScope,
  enabled: boolean,
): QueryResult<CreativeScoreTileData> {
  const trpc = useTRPC();
  return useQuery({ ...trpc.creatives.creativeScoreTile.queryOptions(scope), enabled });
}

export function sortByCreativeScore(
  list: CreativesListData,
  direction: 'asc' | 'desc',
): CreativesListData {
  return sortByField(list, direction, (creative) => creative.creativeScore?.score ?? null);
}

export function sortByField(
  list: CreativesListData,
  direction: 'asc' | 'desc',
  select: (creative: CreativeRow) => number | null,
): CreativesListData {
  const creatives = [...list.creatives].sort((a, b) => {
    const valueA = select(a);
    const valueB = select(b);
    if (valueA === null) return valueB === null ? 0 : 1;
    if (valueB === null) return -1;
    return direction === 'asc' ? valueA - valueB : valueB - valueA;
  });
  return { ...list, creatives };
}

export function mergeCreativeScores(
  list: CreativesListData | undefined,
  scores: CreativeScoresData | undefined,
  wantsCreativeScore: boolean,
): CreativesListData | undefined {
  if (list === undefined) return undefined;
  if (!wantsCreativeScore || scores === undefined) return list;
  return {
    ...list,
    creatives: list.creatives.map((creative) => {
      const creativeScore = scores.scores[creative.creativeId];
      return creativeScore === undefined ? creative : { ...creative, creativeScore };
    }),
  };
}

export function useMetricTrend(
  scope: MetricTrendScope,
  enabled: boolean,
): QueryResult<MetricTrendData | undefined> {
  const trpc = useTRPC();
  const { metric, ...rest } = scope;
  return useQuery({
    ...trpc.creatives.metricTrend.queryOptions({ ...rest, metrics: [metric] }),
    enabled,
    select: ({ metrics, currencySymbol, granularity }) => {
      const series = metrics[0];
      return series === undefined ? undefined : { ...series, currencySymbol, granularity };
    },
  });
}
